import { useRef, useEffect } from "react";
import { useCache } from "@/hooks/useCache";
import { useFilterSpices } from "@/hooks/useFilter";

interface SpiceCarouselProps {
  selectedSpice: number | null;
  onSelect: (id: number) => void;
}

export function SpiceCarousel({ selectedSpice, onSelect }: SpiceCarouselProps) {
  const { data } = useCache();
  const [blendSpices] = useFilterSpices();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const spices = data
    ? [...blendSpices.entries()]
        .map(([id, entry]) => ({ ...data[id], blendQty: entry.quantity }))
        .filter(Boolean)
        .reverse()
    : [];

  const prevSizeRef = useRef(blendSpices.size);

  // Scroll to start when a new spice is added
  useEffect(() => {
    if (blendSpices.size > prevSizeRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
    prevSizeRef.current = blendSpices.size;
  }, [blendSpices.size]);

  // Scroll selected spice into view when selection changes
  useEffect(() => {
    if (!scrollContainerRef.current || !selectedSpice) return;
    const el = scrollContainerRef.current.querySelector(`[data-id="${selectedSpice}"]`) as HTMLElement;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [selectedSpice]);

  return (
    <>
      {/* Horizontal carousel */}
      {spices.length > 0 && (
        <div
          ref={scrollContainerRef}
          className="flex gap-2 overflow-x-auto flex-1 min-w-0"
          style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        >
          {spices.map((spice) => {
            const active = spice.id === selectedSpice;
            return (
              <button
                key={spice.id}
                data-id={spice.id}
                type="button"
                onClick={() => onSelect(spice.id)}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all duration-150 ${
                  active
                    ? "bg-secondary text-main font-semibold"
                    : "text-secondary/70"
                }`}
              >
                <img
                  src={spice.thumbnail}
                  alt={spice.name}
                  className="w-4 h-4 rounded-full object-cover shrink-0"
                />
                <span>{spice.name}</span>
                <span className={active ? "text-main/60" : "text-secondary/40"}>
                  {Math.round(spice.blendQty)}g
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
